import type { City } from 'src/enums/city';

import { sortClubsInDisplayOrderForBuildType } from '../components/common/club-sorter';
import {
  BuildDomain,
  getBuildDomainForBuildType,
  getBuildTypesForBuildDomain,
} from '../enums/build/build-domain';
import { BuildType, getLinkedBuildTypesForBuildType } from '../enums/build/build-type';
import { shouldUseClubRedesign } from '../enums/build/club-redesign-build-state';
import {
  shouldIncludeInEnquiryForms,
  shouldIncludeInLists,
  shouldIncludeInOnlineJoiningLists,
} from '../enums/build/prod-build-state';
import type { ClubInformation } from '../enums/club-information';
import {
  CLUBS_INFORMATION,
  CLUBS_INFORMATION_FOR_CMS,
  getDataFolderNameForClub,
} from '../enums/club-information';

let clubInformationBySiteId: Map<string, ClubInformation> | null = null;
export const getClubInformation = (siteId: string) => {
  if (clubInformationBySiteId === null) {
    clubInformationBySiteId = new Map(CLUBS_INFORMATION.map((c) => [c.siteId, c]));
  }
  const clubInformation = clubInformationBySiteId.get(siteId);

  if (!clubInformation) {
    throw new Error(`No club information found for site ${siteId}`);
  }
  return clubInformation;
};

export const getClubByHyphenatedClubName = (hyphenatedClubName: string): ClubInformation =>
  CLUBS_INFORMATION.find(
    (clubInformation) => clubInformation.hyphenatedClubName === hyphenatedClubName
  )!;

export const getClubForDataFolderName = (dataFolderName: string): ClubInformation | undefined =>
  CLUBS_INFORMATION.find((club) => getDataFolderNameForClub(club) === dataFolderName);

// TODO POD6DJ-3544: Remove, replace with getClubForDataFolderName
export const getCmsClubForDataFolderName = (dataFolderName: string): ClubInformation | undefined =>
  CLUBS_INFORMATION_FOR_CMS.find((club) => getDataFolderNameForClub(club) === dataFolderName);

export const getFullClubs = (): ClubInformation[] =>
  CLUBS_INFORMATION.filter((clubInformation) =>
    shouldIncludeInLists(clubInformation.prodBuildState)
  );

export const getFullClubsForBuildType = (buildType: BuildType): ClubInformation[] =>
  CLUBS_INFORMATION.filter((clubInformation) => clubInformation.buildType === buildType).filter(
    (clubInformation) => shouldIncludeInLists(clubInformation.prodBuildState)
  );

const getClubsOpenForOnlineJoiningForBuildType = (buildType: BuildType): ClubInformation[] =>
  CLUBS_INFORMATION.filter((clubInformation) => clubInformation.buildType === buildType).filter(
    (clubInformation) => shouldIncludeInOnlineJoiningLists(clubInformation.prodBuildState)
  );

const getFullClubsForClubFinder = (
  buildTypes: BuildType[],
  siteIdsForClubFinder: readonly string[]
): ClubInformation[] =>
  getAllClubsForClubFinder(siteIdsForClubFinder).filter((clubInformation) =>
    buildTypes.includes(clubInformation.buildType)
  );

export const getClubsWithSiteIdIn = (siteIds: number[]): ClubInformation[] => {
  const siteIdsAsStrings = siteIds.map((siteId) => siteId.toString());
  return getFullClubs().filter((club) => club && siteIdsAsStrings.includes(club.siteId));
};

const fullClubsInDisplayOrderForBuildType = new Map<BuildType, ClubInformation[] | null>();
export const getFullClubsInDisplayOrderForBuildType = (buildType: BuildType): ClubInformation[] => {
  if (!fullClubsInDisplayOrderForBuildType.has(buildType)) {
    fullClubsInDisplayOrderForBuildType.set(
      buildType,
      sortClubsInDisplayOrderForBuildType(getFullClubsForBuildType(buildType), buildType)
    );
  }
  return fullClubsInDisplayOrderForBuildType.get(buildType) || [];
};

export const getClubsLinkedToBuildType = (buildType: BuildType): ClubInformation[] => {
  const buildTypes = [buildType].concat(getLinkedBuildTypesForBuildType(buildType));

  return buildTypes.flatMap((type) => getFullClubsInDisplayOrderForBuildType(type));
};

export const getListedEnquiryFormClubsLinkedToBuildType = (
  buildType: BuildType
): ClubInformation[] =>
  getClubsLinkedToBuildType(buildType).filter((clubInformation) =>
    shouldIncludeInEnquiryForms(clubInformation.prodBuildState)
  );

export const getEnquiryFormClubsLinkedToBuildType = (buildType: BuildType): ClubInformation[] => {
  const buildTypes = [buildType].concat(getLinkedBuildTypesForBuildType(buildType));

  return buildTypes.flatMap((type) =>
    sortClubsInDisplayOrderForBuildType(
      CLUBS_INFORMATION.filter(
        (clubInformation) =>
          clubInformation.buildType === type &&
          shouldIncludeInEnquiryForms(clubInformation.prodBuildState)
      ),
      type
    )
  );
};

export const getClubsOpenForOnlineJoiningLinkedToBuildType = (
  buildType: BuildType
): ClubInformation[] => {
  const buildTypes = [buildType].concat(getLinkedBuildTypesForBuildType(buildType));
  return buildTypes.flatMap((type) =>
    sortClubsInDisplayOrderForBuildType(getClubsOpenForOnlineJoiningForBuildType(type), buildType)
  );
};

const getClubFinderBuildTypesForBuildDomain = (buildDomain: BuildDomain): BuildType[] => {
  const baseBuildTypes = getBuildTypesForBuildDomain(buildDomain);

  if (buildDomain === BuildDomain.DAVID_LLOYD_UK) {
    return [...baseBuildTypes, BuildType.HARBOUR];
  }
  if (buildDomain === BuildDomain.DAVID_LLOYD_GERMANY) {
    return [...baseBuildTypes, BuildType.MERIDIAN];
  }

  return baseBuildTypes;
};

export const getClubsForClubFinder = (
  buildType: BuildType,
  siteIdsForClubFinder: readonly string[]
): ClubInformation[] => {
  const buildDomain = getBuildDomainForBuildType(buildType);
  const buildTypes = getClubFinderBuildTypesForBuildDomain(buildDomain);
  return getFullClubsForClubFinder(buildTypes, siteIdsForClubFinder);
};

export const getAllClubsForClubFinder = (
  siteIdsForClubFinder: readonly string[]
): ClubInformation[] =>
  getFullClubs().filter((clubInformation) => siteIdsForClubFinder.includes(clubInformation.siteId));

export const getAllCityClubsForClubFinder = (
  city: City,
  siteIdsForClubFinder: readonly string[]
): ClubInformation[] => {
  return getAllClubsForClubFinder(siteIdsForClubFinder).filter(
    (clubInformation) => clubInformation.city === city
  );
};

export const shouldUseClubRedesignForClub = (clubId: string) =>
  shouldUseClubRedesign(getClubInformation(clubId).redesignBuildState);
